// Pure helpers that turn a project (workflow definition + stage assignments)
// into Gantt rows. Kept free of React so the portfolio Gantt can reuse them.

export const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;

const DAY_KEYS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const DEFAULT_WORKING_DAYS = ["MON", "TUE", "WED", "THU", "FRI"];

// Statuses whose bar is still running, so its end is "now" or the expected
// end, whichever is later.
const OPEN_STATUSES = ["PENDING", "IN_PROGRESS", "SUBMITTED", "REJECTED"];

export function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

export function toDateKey(date) {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function parseTime(value, fallback) {
  const [h, m] = String(value || fallback)
    .split(":")
    .map(Number);
  return { hours: h || 0, minutes: m || 0 };
}

// Working calendar built from the org schedule (/schedule/org). Individual
// leave is not applied here — this is the project's calendar, not a person's.
export function buildCalendar(orgSchedule) {
  const workingDays = new Set(
    (orgSchedule?.workingDays?.length
      ? orgSchedule.workingDays
      : DEFAULT_WORKING_DAYS
    ).map((day) => String(day).toUpperCase().slice(0, 3)),
  );
  const holidays = new Map(
    (orgSchedule?.publicHolidays || []).map((holiday) => [
      String(holiday.date).slice(0, 10),
      holiday.name || "Holiday",
    ]),
  );
  const start = parseTime(orgSchedule?.workStartTime, "09:00");
  const end = parseTime(orgSchedule?.workEndTime, "18:00");
  const hoursPerDay = Math.max(
    end.hours + end.minutes / 60 - (start.hours + start.minutes / 60),
    1,
  );

  return {
    workingDays,
    holidays,
    start,
    end,
    hoursPerDay,
    dayType(date) {
      const key = toDateKey(date);
      if (holidays.has(key))
        return { type: "HOLIDAY", label: holidays.get(key) };
      if (!workingDays.has(DAY_KEYS[new Date(date).getDay()]))
        return { type: "WEEKLY_OFF", label: "Weekly off" };
      return { type: "WORKING", label: null };
    },
  };
}

// Stage estimate in working hours, using the org's day length for Days/Weeks.
export function estimateWorkingHours(stage, calendar) {
  const value = Number(stage?.estimatedDuration);
  if (!Number.isFinite(value) || value <= 0) return calendar.hoursPerDay;
  switch (String(stage?.durationUnit || "Days").toLowerCase()) {
    case "minutes":
      return value / 60;
    case "hours":
      return value;
    case "weeks":
      return value * calendar.workingDays.size * calendar.hoursPerDay;
    case "days":
    default:
      return value * calendar.hoursPerDay;
  }
}

export function formatEstimate(stage) {
  const value = Number(stage?.estimatedDuration);
  if (!Number.isFinite(value) || value <= 0) return null;
  const unit = String(stage?.durationUnit || "Days");
  const singular = unit.replace(/s$/i, "");
  return `${value} ${value === 1 ? singular.toLowerCase() : unit.toLowerCase()}`;
}

// Walks forward through working hours only, skipping weekly-offs and holidays.
export function addWorkingHours(from, hours, calendar) {
  let cursor = new Date(from);
  let remaining = hours;
  for (let guard = 0; guard < 3660; guard += 1) {
    if (calendar.dayType(cursor).type === "WORKING") {
      const dayStart = new Date(cursor);
      dayStart.setHours(calendar.start.hours, calendar.start.minutes, 0, 0);
      const dayEnd = new Date(cursor);
      dayEnd.setHours(calendar.end.hours, calendar.end.minutes, 0, 0);
      const begin = cursor > dayStart ? cursor : dayStart;
      if (begin < dayEnd) {
        const available = (dayEnd - begin) / HOUR_MS;
        if (remaining <= available) {
          return new Date(begin.getTime() + remaining * HOUR_MS);
        }
        remaining -= available;
      }
    }
    cursor = startOfDay(addDays(cursor, 1));
  }
  return cursor;
}

// Stage order for the rows: a breadth-first walk from the start stages that
// ignores rework (back) edges, so the chart reads top-to-bottom in flow order.
// Returns the order and the set of forward edges used for dependency lines.
export function orderStages(definition) {
  const stages = Array.isArray(definition?.stages) ? definition.stages : [];
  const connections = Array.isArray(definition?.connections)
    ? definition.connections
    : [];
  const ids = new Set(stages.map((s) => s.id));
  const outgoing = new Map(stages.map((s) => [s.id, []]));
  const incoming = new Map(stages.map((s) => [s.id, 0]));
  connections.forEach((c) => {
    if (!ids.has(c.sourceStageId) || !ids.has(c.targetStageId)) return;
    outgoing.get(c.sourceStageId).push(c.targetStageId);
    incoming.set(c.targetStageId, incoming.get(c.targetStageId) + 1);
  });

  const starts = stages
    .filter((s) => incoming.get(s.id) === 0)
    .map((s) => s.id);
  const queue = starts.length
    ? [...starts]
    : stages.slice(0, 1).map((s) => s.id);
  const rank = new Map();
  while (queue.length) {
    const id = queue.shift();
    if (rank.has(id)) continue;
    rank.set(id, rank.size);
    outgoing.get(id).forEach((next) => {
      if (!rank.has(next)) queue.push(next);
    });
  }
  // Anything unreachable from a start stage keeps its definition order.
  stages.forEach((s) => {
    if (!rank.has(s.id)) rank.set(s.id, rank.size);
  });

  const ordered = [...stages].sort((a, b) => rank.get(a.id) - rank.get(b.id));
  const forwardEdges = connections.filter(
    (c) =>
      ids.has(c.sourceStageId) &&
      ids.has(c.targetStageId) &&
      rank.get(c.sourceStageId) < rank.get(c.targetStageId),
  );
  return { ordered, forwardEdges };
}

function assigneeName(assignment) {
  const a = assignment?.assignee;
  if (!a) return "Unassigned";
  return `${a.firstName || ""} ${a.lastName || ""}`.trim() || "Unnamed user";
}

// Builds one row per workflow stage. Visited stages get one bar per
// assignment (rework visits and co-assignees included); stages the project has
// not reached yet get a projected bar chained after their predecessors, using
// the workflow's estimates and the org working calendar.
export function buildGanttModel(project, calendar, now = new Date()) {
  const definition = project?.workflow?.definition;
  const { ordered, forwardEdges } = orderStages(definition);
  const assignments = project?.stageAssignments || [];
  const stateById = new Map(
    (project?.stageGraph || []).map((entry) => [entry.stageId, entry.state]),
  );
  const projectClosed = ["COMPLETED", "AWAITING_INVOICE"].includes(
    project?.status,
  );

  const predecessorsOf = new Map(ordered.map((s) => [s.id, []]));
  forwardEdges.forEach((edge) => {
    predecessorsOf.get(edge.targetStageId).push(edge.sourceStageId);
  });

  const stageEnd = new Map();
  const projectStart = project?.createdAt ? new Date(project.createdAt) : now;

  const rows = ordered.map((stage) => {
    const estimateHours = estimateWorkingHours(stage, calendar);
    const visits = assignments
      .filter((a) => a.stageId === stage.id && a.status !== "REMOVED")
      .sort((a, b) => new Date(a.assignedAt) - new Date(b.assignedAt));

    const bars = visits.map((assignment) => {
      const start = new Date(assignment.startDate || assignment.assignedAt);
      const expectedEnd = assignment.expectedEndDate
        ? new Date(assignment.expectedEndDate)
        : null;
      // A closed project's leftover open assignment is history, not live work:
      // it must not stretch to today or show as overdue.
      const isOpen =
        !projectClosed && OPEN_STATUSES.includes(assignment.status);
      let end;
      if (assignment.completedAt) end = new Date(assignment.completedAt);
      else if (isOpen) {
        const planned =
          expectedEnd || addWorkingHours(start, estimateHours, calendar);
        end = start > now ? planned : new Date(Math.max(planned, now));
      } else {
        end = expectedEnd || addWorkingHours(start, estimateHours, calendar);
      }
      if (end <= start) end = new Date(start.getTime() + HOUR_MS);

      const overdue =
        isOpen && expectedEnd && now > expectedEnd && start <= now
          ? now - expectedEnd
          : 0;
      const lateFinish =
        assignment.completedAt && expectedEnd
          ? Math.max(new Date(assignment.completedAt) - expectedEnd, 0)
          : 0;

      return {
        id: assignment.id,
        kind: "actual",
        status: assignment.status,
        stageName: stage.name,
        assignee: assigneeName(assignment),
        visitNumber: assignment.visitNumber || 1,
        start,
        end,
        expectedEnd,
        completedAt: assignment.completedAt
          ? new Date(assignment.completedAt)
          : null,
        scheduledFuture: start > now,
        overdueMs: overdue,
        lateFinishMs: lateFinish,
        timeLoggedMinutes: assignment.totalTimeMinutes || 0,
        reviewNote: assignment.reviewNote || null,
        reassignmentReason: assignment.reassignmentReason || null,
      };
    });

    const state =
      stateById.get(stage.id) || (bars.length ? "VISITED" : "UPCOMING");

    if (
      !bars.length &&
      !projectClosed &&
      state !== "SKIPPED" &&
      state !== "DONE"
    ) {
      const predecessorEnds = predecessorsOf
        .get(stage.id)
        .map((id) => stageEnd.get(id))
        .filter(Boolean);
      const anchor = predecessorEnds.length
        ? new Date(Math.max(...predecessorEnds))
        : projectStart;
      const start = new Date(Math.max(anchor, now));
      const end = addWorkingHours(start, estimateHours, calendar);
      bars.push({
        id: `projected-${stage.id}`,
        kind: "projected",
        status: state,
        stageName: stage.name,
        assignee: null,
        start,
        end,
        waitingOn: predecessorsOf.get(stage.id),
      });
    }

    if (bars.length) {
      stageEnd.set(stage.id, Math.max(...bars.map((b) => b.end.getTime())));
    }

    // Overlapping bars (co-assignees, rework) are stacked into lanes.
    const laneEnds = [];
    [...bars]
      .sort((a, b) => a.start - b.start)
      .forEach((bar) => {
        let lane = laneEnds.findIndex((laneEnd) => laneEnd <= bar.start);
        if (lane === -1) {
          lane = laneEnds.length;
          laneEnds.push(0);
        }
        laneEnds[lane] = bar.end;
        bar.lane = lane;
      });

    return {
      stageId: stage.id,
      stageName: stage.name || "Unnamed Stage",
      color: stage.color || null,
      state,
      estimate: formatEstimate(stage),
      assignees: Array.from(
        new Set(bars.filter((b) => b.kind === "actual").map((b) => b.assignee)),
      ),
      lanes: Math.max(laneEnds.length, 1),
      bars,
    };
  });

  // start/end are the bars' own span; min/max also take in "now" so a chart
  // of a finished project still has today in range.
  const allBars = rows.flatMap((row) => row.bars);
  const start = allBars.length
    ? new Date(Math.min(...allBars.map((b) => b.start)))
    : startOfDay(projectStart);
  const end = allBars.length
    ? new Date(Math.max(...allBars.map((b) => b.end)))
    : null;
  const min = start;
  const max = end && end > now ? end : allBars.length ? now : addDays(now, 7);

  const stageNameById = new Map(ordered.map((s) => [s.id, s.name]));
  const dependencies = forwardEdges.map((edge) => ({
    id: `${edge.sourceStageId}->${edge.targetStageId}`,
    from: edge.sourceStageId,
    to: edge.targetStageId,
    fromName: stageNameById.get(edge.sourceStageId),
    toName: stageNameById.get(edge.targetStageId),
  }));

  return { rows, dependencies, start, end, min, max };
}

export function formatSpan(ms) {
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  if (hours < 24) return rem ? `${hours}h ${rem}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours ? `${days}d ${remHours}h` : `${days}d`;
}
